import { forwardRef, useEffect, useState } from 'react';
import classnames from 'classnames';
import Icon from '../../../Icon';
import LoadingDots from '@/components/ui/Buttons/LoadingDots';

interface PanelButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label?: string;
  strikePrice?: string;
  price?: string;
  copy?: string;
  isLive?: boolean;
  sticky?: boolean;
  ariaLabel?: string;
  isSuccess?: boolean;
  successLabel?: string;
  iconName?: string;
  isLoading?: boolean;
  isError?: boolean;
  purchased?: boolean;
}

const PanelButton = forwardRef<HTMLButtonElement, PanelButtonProps>(
  (
    {
      label,
      strikePrice,
      price,
      isLive,
      className,
      isSuccess,
      successLabel,
      ariaLabel,
      iconName,
      isLoading,
      isError,
      purchased,
      ...props
    },
    ref
  ) => {
    const [showSuccess, setShowSuccess] = useState<boolean>(isSuccess ?? false);
    const [showLoading, setShowLoading] = useState<boolean>(isLoading ?? false);

    const buttonClasses = classnames(
      'relative h-[56px] w-full overflow-hidden rounded',
      {
        'bg-red text-white hover:bg-red-dark active:bg-red-dark active:text-opacity-70': !isLive,
        'before:absolute before:inset-0 before:size-full before:bg-gradient before:transition hover:bg-black before:hover:opacity-80':
          isLive
      },
      className
    );

    const buttonInnerClasses = classnames(
      'relative z-1 flex items-center rounded px-6 text-white transition lg:px-8',
      {
        'justify-between': (price || iconName) && !showLoading && (!showSuccess || successLabel),
        'justify-center':
          !((price || iconName) && !showLoading && (!showSuccess || successLabel)) ||
          showLoading ||
          purchased
      },
      className
    );

    const currentAriaLabel = showLoading
      ? 'Loading'
      : showSuccess && successLabel
        ? successLabel
        : ariaLabel;
    const shouldDisableForSuccess = showSuccess && !successLabel;

    useEffect(() => {
      if (isSuccess) {
        setShowSuccess(isSuccess);
        setShowLoading(false);
        const timeout = setTimeout(() => {
          setShowSuccess(false);
        }, 1000);
        return () => clearTimeout(timeout);
      }

      setShowLoading(!!isLoading && !isError);
    }, [isSuccess, isLoading, isError]);

    return (
      <button
        onClick={shouldDisableForSuccess ? () => {} : props.onClick}
        {...props}
        className={buttonClasses}
        disabled={shouldDisableForSuccess || showLoading}
        aria-label={currentAriaLabel}
        ref={ref}
        data-component={'PanelButton'}
      >
        <div className={buttonInnerClasses}>
          {!showLoading && (
            <span className="text-1 font-bold">
              {showSuccess && successLabel ? successLabel : label}
            </span>
          )}
          {(strikePrice || price) &&
            (!showSuccess || successLabel) &&
            !showLoading &&
            !purchased && (
              <div className="label">
                {strikePrice && <span className="mr-2 line-through">{strikePrice}</span>}
                {price && <span>{price}</span>}
              </div>
            )}
          {(showSuccess || iconName) && !isLoading && (
            <Icon name={showSuccess && !successLabel ? 'check' : (iconName ?? '')} size="medium" />
          )}
          {showLoading && !showSuccess && <LoadingDots color="white" />}
        </div>
      </button>
    );
  }
);

PanelButton.displayName = 'PanelButton';
export default PanelButton;
